import React, { useState, useEffect } from 'react';
import { GRID_W, GRID_H } from '../constants/gameConstants';

export const BattleAnimation = React.memo(({ battle, onComplete }) => {
    const [phase, setPhase] = useState('idle');
    const [showExplosion, setShowExplosion] = useState(false);
    const [arrowPosition, setArrowPosition] = useState({ x1: 0, y1: 0, x2: 0, y2: 0 });

    useEffect(() => {
        if (!battle) return;

        const calculatePositions = () => {
            const { from, to } = battle;
            // Dinamik hesaplama
            const cellWidth = 100 / GRID_W;
            const cellHeight = 100 / GRID_H;
            
            return {
                x1: (from.x + 0.5) * cellWidth,
                y1: (from.y + 0.5) * cellHeight,
                x2: (to.x + 0.5) * cellWidth,
                y2: (to.y + 0.5) * cellHeight
            };
        };

        const sequence = async () => {
            const positions = calculatePositions();
            setArrowPosition(positions);
            
            setPhase('preparing');
            await new Promise(resolve => setTimeout(resolve, 300));

            setPhase('attacking');
            await new Promise(resolve => setTimeout(resolve, 800));

            if (battle.success || battle.criticalHit) {
                setShowExplosion(true);
                await new Promise(resolve => setTimeout(resolve, 500));
                setShowExplosion(false);
            }

            setPhase('result');
            await new Promise(resolve => setTimeout(resolve, 700));

            setPhase('complete');
            onComplete?.();
        };

        sequence();
    }, [battle, onComplete]);

    if (!battle || phase === 'complete') return null;

    const { to, success, criticalHit, attackerColor, defenderColor, from } = battle;

    return (
        <>
            {/* Saldıran hücre vurgulama */}
            <div 
                className="absolute bg-transparent border-2 rounded pointer-events-none z-40"
                style={{
                    left: `${(from.x / GRID_W) * 100}%`,
                    top: `${(from.y / GRID_H) * 100}%`,
                    width: `${100 / GRID_W}%`,
                    height: `${100 / GRID_H}%`,
                    borderColor: attackerColor,
                    boxShadow: `0 0 20px ${attackerColor}, inset 0 0 15px ${attackerColor}`,
                    animation: phase === 'attacking' 
                        ? 'attackerPulse 0.6s ease-in-out infinite' 
                        : 'attackerGlow 0.5s ease-in-out infinite alternate',
                }}
            />

            {/* Hedef hücre vurgulama */}
            <div 
                className="absolute bg-transparent border-2 rounded pointer-events-none z-40"
                style={{
                    left: `${(to.x / GRID_W) * 100}%`,
                    top: `${(to.y / GRID_H) * 100}%`,
                    width: `${100 / GRID_W}%`,
                    height: `${100 / GRID_H}%`,
                    borderColor: success ? attackerColor : defenderColor,
                    boxShadow: `0 0 20px ${success ? attackerColor : defenderColor}`,
                    animation: phase === 'result' 
                        ? (success ? 'conqueredExpand 0.6s ease-out' : 'defendedFlash 0.6s ease-out') 
                        : 'defenderShake 0.3s ease-in-out',
                }}
            />

            {/* Ok animasyonu */}
            {phase === 'attacking' && (
                <svg 
                    className="absolute top-0 left-0 w-full h-full pointer-events-none z-50"
                    style={{ overflow: 'visible' }}
                >
                    <defs>
                        <linearGradient id="arrow-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" style={{ stopColor: attackerColor, stopOpacity: 1 }} />
                            <stop offset="100%" style={{ stopColor: attackerColor, stopOpacity: 0.3 }} />
                        </linearGradient>
                        <marker
                            id="arrowhead"
                            markerWidth="10"
                            markerHeight="7"
                            refX="9"
                            refY="3.5"
                            orient="auto"
                        >
                            <polygon points="0 0, 10 3.5, 0 7" fill={attackerColor} />
                        </marker>
                    </defs>
                    <line
                        x1={`${arrowPosition.x1}%`} y1={`${arrowPosition.y1}%`}
                        x2={`${arrowPosition.x2}%`} y2={`${arrowPosition.y2}%`}
                        stroke="url(#arrow-gradient)"
                        strokeWidth="3"
                        markerEnd="url(#arrowhead)"
                        style={{
                            strokeDasharray: 1000,
                            strokeDashoffset: phase === 'attacking' ? 1000 : 0,
                            animation: 'arrowShoot 0.8s ease-out forwards',
                            filter: 'drop-shadow(0 0 5px currentColor)'
                        }}
                    />
                </svg>
            )}

            {/* Patlama efekti */}
            {showExplosion && (
                <div 
                    className="absolute pointer-events-none z-60 flex items-center justify-center"
                    style={{
                        left: `${(to.x / GRID_W) * 100}%`,
                        top: `${(to.y / GRID_H) * 100}%`,
                        width: `${100 / GRID_W}%`,
                        height: `${100 / GRID_H}%`,
                    }}
                >
                    <div className="absolute w-full h-full border-2 rounded-full animate-explosionExpand"
                         style={{ borderColor: criticalHit ? 'rgba(255, 0, 100, 0.9)' : 'rgba(255, 100, 0, 0.8)' }} />
                    <div className="absolute w-full h-full border-2 rounded-full animate-explosionExpand animation-delay-100"
                         style={{ borderColor: criticalHit ? 'rgba(255, 0, 100, 0.7)' : 'rgba(255, 150, 0, 0.6)' }} />
                    {criticalHit && (
                        <div className="text-2xl animate-criticalBounce" style={{ filter: 'drop-shadow(0 0 10px #ff0066)' }}>💥</div>
                    )}
                </div>
            )}

            {/* Sonuç göstergesi */}
            {phase === 'result' && (
                <div 
                    className="absolute pointer-events-none z-70 flex items-center justify-center text-2xl animate-resultPop"
                    style={{
                        left: `${(to.x / GRID_W) * 100}%`,
                        top: `${(to.y / GRID_H) * 100}%`,
                        width: `${100 / GRID_W}%`,
                        height: `${100 / GRID_H}%`,
                        filter: 'drop-shadow(0 0 8px rgba(255, 255, 255, 0.8))',
                    }}
                >
                    {success ? '⚔️' : '🛡️'}
                </div>
            )}
        </>
    );
});

export const useBattleAnimations = () => {
    const [currentBattle, setCurrentBattle] = useState(null);
    const [battleQueue, setBattleQueue] = useState([]);

    useEffect(() => {
        if (!currentBattle && battleQueue.length > 0) {
            const [nextBattle, ...rest] = battleQueue;
            setCurrentBattle(nextBattle);
            setBattleQueue(rest);
        }
    }, [currentBattle, battleQueue]);

    const addBattle = (battleData) => {
        setBattleQueue(prev => [...prev, battleData]);
    };

    const completeBattle = () => {
        setCurrentBattle(null);
    };

    return {
        currentBattle,
        addBattle,
        completeBattle,
        hasPendingBattles: currentBattle !== null || battleQueue.length > 0
    };
};